import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChaos } from './ChaosContext';
import { useStorm } from '../storm/StormContext';
import { useMagneticLetters, MagnetApi } from '../play/MagneticText';

// The MANIA mark's animation as a reusable type treatment: each letter sits in the
// handwriting face (.letter-normal) and a random letter swaps into the serif
// (.letter-highlight) every tempoMs, with a periodic hue glitch + .text-flicker.
// `resolve` makes letters materialize out of noise first (AI follow-ups, archive labels).
// `magnetic` (default on): letters shy away from the pointer, scatter on a flick, and a tap
// fires a glitch burst when `tapBurst` is set (the MANIA mark only). Letter offsets are written by the play loop, never React state.
const NOISE = '#%&@$*+=?!/\\|<>~';

type Tag = 'h1' | 'h2' | 'span' | 'p';

const GlitchText: React.FC<{
  text: string; as?: Tag; style?: React.CSSProperties; className?: string;
  resolve?: boolean; seedKey?: string; tempoMs?: number; magnetic?: boolean; tapBurst?: boolean;
}> = ({ text, as = 'span', style, className, resolve = false, seedKey, tempoMs = 220, magnetic = true, tapBurst = false }) => {
  const { rngFor } = useChaos();
  const { reducedMotion } = useStorm();
  const rng = useMemo(() => rngFor(seedKey ?? text), [rngFor, seedKey, text]);
  const [hi, setHi] = useState(-1);
  const [glitch, setGlitch] = useState(false);
  const [settled, setSettled] = useState(resolve && !reducedMotion ? 0 : text.length);
  const rootRef = useRef<HTMLElement>(null);
  const burstTimer = useRef(0);
  const onTap = useCallback((magnet: MagnetApi) => {
    setGlitch(true);
    window.clearTimeout(burstTimer.current);
    burstTimer.current = window.setTimeout(() => setGlitch(false), 300);
    magnet.impulse(6); // letter jitter, ±6px
  }, []);
  useEffect(() => () => window.clearTimeout(burstTimer.current), []);
  useMagneticLetters(rootRef, { reduced: reducedMotion || !magnetic, onTap: tapBurst ? onTap : undefined });

  useEffect(() => {
    if (settled >= text.length) return;
    const t = window.setInterval(() => setSettled((s) => s + 1), Math.max(25, 700 / Math.max(1, text.length)));
    return () => window.clearInterval(t);
  }, [settled, text.length]);

  useEffect(() => {
    if (reducedMotion) return;
    const cycle = window.setInterval(() => setHi(Math.floor(rng() * text.length)), tempoMs);
    let off = 0;
    const g = window.setInterval(() => {
      if (rng() > 0.7) { setGlitch(true); off = window.setTimeout(() => setGlitch(false), 150); }
    }, 1200);
    return () => { window.clearInterval(cycle); window.clearInterval(g); window.clearTimeout(off); setGlitch(false); };
  }, [reducedMotion, rng, text.length, tempoMs]);

  let idx = 0;
  const words = text.split(' ').map((w, wi, all) => {
    const letters = w.split('').map((ch) => ({ ch, i: idx++ }));
    return wi < all.length - 1 ? letters.concat([{ ch: ' ', i: idx++ }]) : letters;
  });
  const Tag = as as React.ElementType;
  return (
    <Tag ref={rootRef} aria-label={text} className={`mania-title${reducedMotion ? '' : ' text-flicker'} ${className ?? ''}`}
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', margin: 0,
        filter: glitch ? 'hue-rotate(90deg) brightness(1.4)' : 'none', transition: 'filter 0.1s', ...style }}>
      {words.map((letters, wi) => (
        <span key={wi} aria-hidden style={{ display: 'inline-flex', whiteSpace: 'pre' }}>
          {letters.map(({ ch, i }) => {
            const shown = i < settled || ch === ' ' ? ch : NOISE[Math.floor(rng() * NOISE.length)];
            return <span key={i} data-letter="" className={hi === i ? 'letter-highlight' : 'letter-normal'}>{shown}</span>;
          })}
        </span>
      ))}
    </Tag>
  );
};

export default GlitchText;
